import wNumb from "wnumb";

import { create, axisTop, axisLeft, scaleBand, select } from 'd3';

import { IEtfCorrelationMapBackend } from 'api/correlations';
import { D3Portal, D3PortalPropsChartSize } from 'shared/ui-kit';
import { useMemo } from 'react';

import { MapCorrelationSettings } from '../model'

import { scaleLinear } from 'd3-scale';

import styles from './content.module.scss';
import { decodeSiteResolutionValue, SiteResolution, useSiteResolution } from "shared/mechanics/site-resolution";

const digit2Format = wNumb({ decimals: 2, suffix: '' }).to;

export interface MapCorrelationsContentProps {
	settings: MapCorrelationSettings;
	map: IEtfCorrelationMapBackend;

	highlighEtfTicker: string | null;
}

export const MapCorrelationsContent = (props: MapCorrelationsContentProps) => {
	const siteResolution = useSiteResolution();

	const generateSvg = useMemo(() => (size: D3PortalPropsChartSize) => generateChart(props.map, size, siteResolution, props.highlighEtfTicker), [props.map, siteResolution]);

	return (
		<div style={{ flex: '1', position: 'relative', padding: '5px' }}>
			<D3Portal generateSvg={generateSvg} />
			<div className={styles.tooltip}></div>
		</div>
	);
}

function generateChart(map: IEtfCorrelationMapBackend, size: D3PortalPropsChartSize, siteResolution: SiteResolution, highlighEtfTicker: string | null): SVGSVGElement {
	const data = [];
	for (let i = 0; i < map.etfs.length; i++) {
		for (let j = 0; j < map.etfs.length; j++) {
			const xItem = map.etfs[i];
			const yItem = map.etfs[j];

			const value = i === j
				? 1
				: map.values[`${xItem.key}:${yItem.key}`] || map.values[`${yItem.key}:${xItem.key}`] || 0;

			data.push({ x: xItem.ticker, y: yItem.ticker, val: value });
		}
	}

	const treemapSize: any = [0, 0, size.width, size.height];

	const svg = create('svg').attr('viewBox', treemapSize);

	const colorScale = scaleLinear([-1, 0, 0.3, 1], ['#EE3D48', 'rgb(65,69,84)', 'rgb(60, 84, 89)', '#70E852'])

	const leftAxisSize = 50;
	const topAxisSize = decodeSiteResolutionValue(siteResolution, 50, 0);

	const topAxisDomain = scaleBand().domain(map.etfs.map(x => x.ticker)).range([0, size.width - leftAxisSize - 1]).paddingInner(0.1);
	if (topAxisSize !== 0) {
		const topAxis = axisTop(topAxisDomain);
		svg.append("g").style('transform', () => `translate(${leftAxisSize}px, ${topAxisSize - 10}px)`).classed(styles.axis, true).call(topAxis)
			.selectAll("text")
				.style("text-anchor", "end")
				.attr("dx", "40px")
				.attr("dy", "12px")
				.attr("transform", "rotate(-90)");
	}
	

	const leftAxisDomain = scaleBand().domain(map.etfs.map(x => x.ticker)).range([0, size.height - topAxisSize - 1]).paddingInner(0.1);
	const leftAxis = axisLeft(leftAxisDomain);
	svg.append("g").style('transform', () => `translate(${leftAxisSize - 10}px, ${topAxisSize}px)`).classed(styles.axis, true).call(leftAxis);

	svg.selectAll("rect")
		.data(data)
		.enter()
		.append("rect")
			.attr("x", item => leftAxisSize + topAxisDomain(item.x)!)
			.attr("y", item => topAxisSize + leftAxisDomain(item.y)!)
			.attr("width", topAxisDomain.bandwidth())
			.attr("height", leftAxisDomain.bandwidth())
			.style("fill", item => colorScale(item.val))
		.on('mouseover', (event: MouseEvent, data: any) => {
			const rect = event.target as SVGRectElement;

			svg.selectAll('rect').filter((item: any) => item.x == data.x || item.y == data.y).classed(styles.selected_same, true);
			select(rect).classed(styles.selected_concrete, true);

			select('.' + styles.tooltip)
				.style("display", "block")
				.html(`${data.x} vs ${data.y}: ${digit2Format(data.val)}`);

			const tooltipRect = (select('.' + styles.tooltip)!.node() as Element)!.getBoundingClientRect()
			const svgRect = (svg.node() as Element)!.getBoundingClientRect();

			var row_pos = topAxisDomain(data.x)!;
			var col_pos = leftAxisDomain(data.y)!;
			var tip_width = tooltipRect.width;
			var tip_height = tooltipRect.height;
			var grid_left = svgRect.left;
			var grid_top = svgRect.top;

			var left = grid_left + row_pos + leftAxisSize + (topAxisDomain.bandwidth() / 2) - (tip_width / 2);
			var top = grid_top + col_pos + topAxisSize - tip_height - 5;

			select('.' + styles.tooltip)
				.style('top', top + 'px')
				.style('left', left + 'px');
		})
		.on('mouseout', () => {
			svg.selectAll('rect')
				.classed(styles.selected_same, false)
				.classed(styles.selected_concrete, false);

			select(styles.tooltip)
				.style("display", "none");
		});

	if (highlighEtfTicker !== null) {
		svg.selectAll('rect').filter((item: any) => item.x == highlighEtfTicker || item.y == highlighEtfTicker).classed(styles.hightlight, true);
	}

	return svg.node()!;
}